/**
 * Public access-key URLs are bearer credentials.
 *
 * The attestation link, the lodgement summary and the insurer invoice view all resolve a
 * record by an unguessable key carried in the URL; invite, password-reset and lodgement v1 links
 * carry their secrets as a query parameter; and the hosted lodgement v2 pages (`/lodge/resume/…`
 * and `/lodge/summary/…`) carry theirs as `?accessKey=` and `?summaryToken=`. Any telemetry that
 * records the page URL — PostHog's `$current_url` / `$pathname` / `$referrer`, Sentry's
 * `request.url`, navigation breadcrumbs, span data — would otherwise ship those credentials to a
 * third party. These helpers strip the secret segment and leave the route shape intact, so the
 * telemetry stays useful.
 *
 * The hosted v2 pages keep their public id in the path (it is an identifier, not a credential), so
 * they are not redacted, but they are recording-gated: a session recording of a resumed draft
 * would capture the claimant's answers and the link that authenticates them.
 *
 * The credential catalogue itself lives in `@shared/utils/core/credential-url`, so the server's
 * email click tracking and stored click URLs redact exactly the same shapes.
 */

import {
    CREDENTIAL_PATH_PATTERNS,
    CREDENTIAL_QUERY_PARAM_PATTERN,
    REDACTED_CREDENTIAL,
    redactCredentialUrl,
    redactCredentialUrlsDeep,
} from '@shared/utils/core/credential-url';

export const REDACTED_ACCESS_KEY = REDACTED_CREDENTIAL;

/**
 * Pages that are authenticated by a secret in their query string. Their paths are not redacted
 * (the public id is not a credential) but session recording stays off on them even when the
 * query has already been consumed or rewritten.
 */
const SECRET_PAGE_PATH_PATTERNS: readonly RegExp[] = [/^\/lodge\/(?:resume|summary)\/[^/?#\s"'<>]+/i];

/** Whether a pathname addresses a record by an access key (e.g. to skip session recording). */
export function isAccessKeyPath(pathname: string): boolean {
    return CREDENTIAL_PATH_PATTERNS.some((pattern) => pattern.test(pathname));
}

/**
 * Whether a location carries a secret in its path or its query string — the recording gate for
 * Sentry Replay and PostHog session recording. `search` is the raw `?a=b` string (with or without
 * the leading `?`); the query pattern anchors on the `?`/`&` delimiter, so a bare string is given one.
 */
export function hasSecretInLocation(pathname: string, search: string): boolean {
    // The start page can reveal a resume credential without navigating (Finish later).
    if (
        pathname.toLowerCase().startsWith('/lodge/') ||
        isAccessKeyPath(pathname) ||
        SECRET_PAGE_PATH_PATTERNS.some((pattern) => pattern.test(pathname))
    ) {
        return true;
    }
    if (search.length === 0) {
        return false;
    }
    const delimited = search.startsWith('?') || search.startsWith('&') ? search : `?${search}`;
    return CREDENTIAL_QUERY_PARAM_PATTERN.test(delimited);
}

/** Redact every access-key path segment and secret query parameter in a string. */
export function redactAccessKeys(text: string): string {
    return redactCredentialUrl(text);
}

/**
 * Redact access keys in every string reachable from `value` through plain objects and arrays.
 * Other values (numbers, dates, class instances, functions) are returned unchanged. Returns
 * the same reference when nothing needed redacting, so untouched telemetry is not re-allocated.
 */
export function redactAccessKeysDeep<T>(value: T): T {
    return redactCredentialUrlsDeep(value);
}
